"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.INI = void 0;
const parsimmon_1 = __importDefault(require("parsimmon"));
class INI {
    constructor(buffer) {
        this.space = parsimmon_1.default.optWhitespace;
        this.expression = parsimmon_1.default.regexp(/{.+?}|(([a-z])([A-Za-z\d]+))/);
        this.numbers = parsimmon_1.default.regexp(/[0-9.E-]*/);
        this.name = parsimmon_1.default.regexp(/[0-9a-z_\\-]*/i);
        this.equal = parsimmon_1.default.string('=');
        this.quote = parsimmon_1.default.string('"');
        this.quotes = [this.quote, this.quote];
        this.comma = parsimmon_1.default.string(',');
        this.size = parsimmon_1.default.regexp(/U08|S08|U16|S16|U32|S32|S64|F32|ASCII/);
        this.delimiter = [this.space, this.comma, this.space];
        this.notQuote = parsimmon_1.default.regexp(/[^"]*/);
        this.sqrBrackets = [parsimmon_1.default.string('['), parsimmon_1.default.string(']')];
        this.inQuotes = this.notQuote.trim(this.space).wrap(...this.quotes);
        this.values = parsimmon_1.default.regexp(/[^,;]*/).trim(this.space).sepBy(this.comma);
        this.lines = (new TextDecoder()).decode(buffer).split('\n');
        this.currentPage = undefined;
        this.currentDialog = undefined;
        this.currentPanel = undefined;
        this.currentMenu = undefined;
        this.currentCurve = undefined;
        this.currentTable = undefined;
        this.result = {
            megaTune: {
                signature: '',
                MTversion: 0,
                queryCommand: '',
                versionInfo: '',
            },
            tunerStudio: {
                iniSpecVersion: 0,
            },
            defines: {},
            pcVariables: {},
            constants: {
                pages: [],
            },
            menus: {},
            dialogs: {},
            curves: {},
            tables: {},
            outputChannels: {},
            datalog: {},
            help: {},
        };
    }
    parse() {
        this.parseSections();
        return this;
    }
    getResults() {
        return this.result;
    }
    parseSections() {
        let section;
        this.lines.forEach((raw) => {
            const line = raw.trim();
            if (line === '' || line.startsWith(';') || (line.startsWith('#') && !line.startsWith('#define'))) {
                return;
            }
            const result = parsimmon_1.default
                .seqObj(['section', parsimmon_1.default.letters.wrap(parsimmon_1.default.string('['), parsimmon_1.default.string(']'))], parsimmon_1.default.all).parse(line);
            if (result.status) {
                section = result.value.section.trim();
                return;
            }
            if (section) {
                this.parseSectionLine(section, line);
            }
        });
    }
    parseSectionLine(section, line) {
        switch (section) {
            case 'MegaTune':
                this.parseKeyValueFor('megaTune', line);
                break;
            case 'TunerStudio':
                this.parseKeyValueFor('tunerStudio', line);
                break;
            case 'PcVariables':
                this.parsePcVariables(line);
                break;
            case 'Constants':
                this.parseConstants(line);
                break;
            case 'Menu':
                this.parseMenu(line);
                break;
            case 'SettingContextHelp':
                this.parseKeyValueFor('help', line);
                break;
            case 'UserDefined':
                this.parseDialogs(line);
                break;
            case 'CurveEditor':
                this.parseCurves(line);
                break;
            case 'TableEditor':
                this.parseTables(line);
                break;
            case 'OutputChannels':
                this.parseOutputChannels(line);
                break;
            case 'Datalog':
                this.parseDatalog(line);
                break;
            default:
                break;
        }
    }
    parseDatalog(line) {
        const base = [
            parsimmon_1.default.string('entry'),
            this.space, this.equal, this.space,
            ['name', this.name],
            ...this.delimiter,
        ];
        const type = [
            ...this.delimiter,
            ['type', parsimmon_1.default.regexp(/float|int/)],
        ];
        const format = [
            ...this.delimiter,
            ['format', this.notQuote.wrap(...this.quotes)],
        ];
        const noConditions = [
            ...base,
            ['label', this.notQuote.wrap(...this.quotes)],
            ...type,
            ...format,
        ];
        const withConditions = [
            ...noConditions,
            ...this.delimiter,
            ['condition', this.expression],
        ];
        const labelExpression = [
            ...base,
            ['label', this.expression],
            ...type,
            ...format,
        ];
        const labelExpressionWithCondition = [
            ...labelExpression,
            ...this.delimiter,
            ['condition', this.expression],
        ];
        const result = parsimmon_1.default.seqObj(...labelExpressionWithCondition, parsimmon_1.default.all)
            .or(parsimmon_1.default.seqObj(...withConditions, parsimmon_1.default.all))
            .or(parsimmon_1.default.seqObj(...labelExpression, parsimmon_1.default.all))
            .or(parsimmon_1.default.seqObj(...noConditions, parsimmon_1.default.all))
            .tryParse(line);
        this.result.datalog[result.name] = {
            name: result.name,
            label: INI.sanitize(result.label),
            type: result.type,
            format: INI.sanitize(result.format),
            condition: result.condition ? INI.sanitize(result.condition) : '',
        };
    }
    parseOutputChannels(line) {
        try {
            const result = this.parseConstAndVar(line);
            this.result.outputChannels[result.name] = {
                type: result.type,
                size: result.size,
                offset: Number(result.offset),
                units: INI.sanitize(result.units),
                scale: INI.isNumber(result.scale) ? Number(result.scale) : INI.sanitize(result.scale),
                transform: INI.isNumber(result.transform) ? Number(result.transform) : INI.sanitize(result.transform),
            };
            return;
        }
        catch (_) {
            const base = [
                ['name', this.name],
                this.space, this.equal, this.space,
            ];
            const result = parsimmon_1.default
                .seqObj(...base, ['value', this.notQuote.wrap(...this.quotes)], parsimmon_1.default.all)
                .or(parsimmon_1.default.seqObj(...base, ['value', this.expression], parsimmon_1.default.all))
                .or(parsimmon_1.default.seqObj(...base, ['value', this.numbers], parsimmon_1.default.all))
                .tryParse(line);
            this.result.outputChannels[result.name] = {
                value: INI.sanitize(result.value),
            };
        }
    }
    parseTables(line) {
        const tableResult = parsimmon_1.default.seqObj(parsimmon_1.default.string('table'), this.space, this.equal, this.space, ['name', this.name], ...this.delimiter, ['map', this.name], ...this.delimiter, ['title', this.inQuotes], ...this.delimiter, ['page', parsimmon_1.default.digits], parsimmon_1.default.all).parse(line);
        if (tableResult.status) {
            this.currentTable = tableResult.value.name;
            this.result.tables[this.currentTable] = {
                map: tableResult.value.map,
                title: INI.sanitize(tableResult.value.title),
                page: Number(tableResult.value.page),
                xBins: [],
                yBins: [],
                xyLabels: [],
                zBins: [],
                gridHeight: 0,
                gridOrient: [],
                upDownLabel: [],
            };
            return;
        }
        const helpResult = parsimmon_1.default.seqObj(parsimmon_1.default.string('topicHelp'), this.space, this.equal, this.space, ['help', this.inQuotes], parsimmon_1.default.all).parse(line);
        if (helpResult.status) {
            if (!this.currentTable) {
                throw new Error('Table not set');
            }
            this.result.tables[this.currentTable].help = INI.sanitize(helpResult.value.help);
            return;
        }
        const parseBins = (name) => parsimmon_1.default.seqObj(parsimmon_1.default.string(name), this.space, this.equal, this.space, ['values', this.values], parsimmon_1.default.all).parse(line);
        const xBinsResult = parseBins('xBins');
        if (xBinsResult.status) {
            if (!this.currentTable) {
                throw new Error('Table not set');
            }
            this.result.tables[this.currentTable].xBins = xBinsResult
                .value
                .values
                .map(INI.sanitize);
            return;
        }
        const yBinsResult = parseBins('yBins');
        if (yBinsResult.status) {
            if (!this.currentTable) {
                throw new Error('Table not set');
            }
            this.result.tables[this.currentTable].yBins = yBinsResult
                .value
                .values
                .map(INI.sanitize);
            return;
        }
        const yxLabelsResult = parseBins('xyLabels');
        if (yxLabelsResult.status) {
            if (!this.currentTable) {
                throw new Error('Table not set');
            }
            this.result.tables[this.currentTable].xyLabels = yxLabelsResult
                .value
                .values
                .map(INI.sanitize);
            return;
        }
        const zBinsResult = parseBins('zBins');
        if (zBinsResult.status) {
            if (!this.currentTable) {
                throw new Error('Table not set');
            }
            this.result.tables[this.currentTable].zBins = zBinsResult
                .value
                .values
                .map(INI.sanitize);
            return;
        }
        const gridHeightResult = parsimmon_1.default.seqObj(parsimmon_1.default.string('gridHeight'), this.space, this.equal, this.space, ['gridHeight', this.numbers], parsimmon_1.default.all).parse(line);
        if (gridHeightResult.status) {
            if (!this.currentTable) {
                throw new Error('Table not set');
            }
            this.result.tables[this.currentTable].gridHeight = Number(gridHeightResult.value.gridHeight);
            return;
        }
        const gridOrientResult = parseBins('gridOrient');
        if (gridOrientResult.status) {
            if (!this.currentTable) {
                throw new Error('Table not set');
            }
            this.result.tables[this.currentTable].gridOrient = gridOrientResult
                .value
                .values
                .map(Number);
            return;
        }
        const upDownResult = parseBins('upDownLabel');
        if (upDownResult.status) {
            if (!this.currentTable) {
                throw new Error('Table not set');
            }
            this.result.tables[this.currentTable].upDownLabel = upDownResult
                .value
                .values
                .map(INI.sanitize);
        }
    }
    parseCurves(line) {
        const curveResult = parsimmon_1.default.seqObj(parsimmon_1.default.string('curve'), this.space, this.equal, this.space, ['name', this.name], ...this.delimiter, ['title', this.inQuotes], parsimmon_1.default.all).parse(line);
        if (curveResult.status) {
            this.currentCurve = curveResult.value.name;
            this.result.curves[this.currentCurve] = {
                title: INI.sanitize(curveResult.value.title),
                labels: [],
                xAxis: [],
                yAxis: [],
                xBins: [],
                yBins: [],
                size: [],
            };
            return;
        }
        const labelsResult = parsimmon_1.default.seqObj(parsimmon_1.default.string('columnLabel'), this.space, this.equal, this.space, ['labels', this.values], parsimmon_1.default.all).parse(line);
        if (labelsResult.status) {
            if (!this.currentCurve) {
                throw new Error('Curve not set');
            }
            this.result.curves[this.currentCurve].labels = labelsResult
                .value
                .labels
                .map(INI.sanitize);
            return;
        }
        const parseAxis = (name) => parsimmon_1.default.seqObj(parsimmon_1.default.string(name), this.space, this.equal, this.space, ['values', this.values], parsimmon_1.default.all).parse(line);
        const xAxisResult = parseAxis('xAxis');
        if (xAxisResult.status) {
            if (!this.currentCurve) {
                throw new Error('Curve not set');
            }
            this.result.curves[this.currentCurve].xAxis = xAxisResult
                .value
                .values
                .map((val) => INI.isNumber(val) ? Number(val) : INI.sanitize(val));
            return;
        }
        const yAxisResult = parseAxis('yAxis');
        if (yAxisResult.status) {
            if (!this.currentCurve) {
                throw new Error('Curve not set');
            }
            this.result.curves[this.currentCurve].yAxis = yAxisResult
                .value
                .values
                .map((val) => INI.isNumber(val) ? Number(val) : INI.sanitize(val));
            return;
        }
        const xBinsResult = parseAxis('xBins');
        if (xBinsResult.status) {
            if (!this.currentCurve) {
                throw new Error('Curve not set');
            }
            this.result.curves[this.currentCurve].xBins = xBinsResult
                .value
                .values
                .map((val) => INI.isNumber(val) ? Number(val) : INI.sanitize(val));
            return;
        }
        const yBinsResult = parseAxis('yBins');
        if (yBinsResult.status) {
            if (!this.currentCurve) {
                throw new Error('Curve not set');
            }
            this.result.curves[this.currentCurve].yBins = yBinsResult
                .value
                .values
                .map((val) => INI.isNumber(val) ? Number(val) : INI.sanitize(val));
            return;
        }
        const size = parseAxis('size');
        if (size.status) {
            if (!this.currentCurve) {
                throw new Error('Curve not set');
            }
            this.result.curves[this.currentCurve].size = size
                .value
                .values
                .map((val) => INI.isNumber(val) ? Number(val) : INI.sanitize(val));
        }
    }
    parseDialogs(line) {
        const dialogBase = [
            parsimmon_1.default.string('dialog'),
            this.space, this.equal, this.space,
            ['name', this.name],
            ...this.delimiter,
            ['title', this.inQuotes],
        ];
        const dialogResult = parsimmon_1.default
            .seqObj(...dialogBase, ...this.delimiter, ['layout', this.name], parsimmon_1.default.all)
            .or(parsimmon_1.default.seqObj(...dialogBase, parsimmon_1.default.all))
            .parse(line);
        if (dialogResult.status) {
            this.currentDialog = dialogResult.value.name;
            this.result.dialogs[this.currentDialog] = {
                title: INI.sanitize(dialogResult.value.title),
                layout: dialogResult.value.layout,
                panels: {},
                fields: [],
            };
            return;
        }
        const panelBase = [
            parsimmon_1.default.string('panel'),
            this.space, this.equal, this.space,
            ['name', this.name],
        ];
        const panelWithLayout = [
            ...panelBase,
            ...this.delimiter,
            ['layout', this.name],
        ];
        const panelWithCondition = [
            ...panelBase,
            ...this.delimiter,
            ['condition', this.expression],
        ];
        const panelResult = parsimmon_1.default
            .seqObj(...panelWithLayout, ...this.delimiter, ['condition', this.expression], parsimmon_1.default.all)
            .or(parsimmon_1.default.seqObj(...panelWithCondition, parsimmon_1.default.all))
            .or(parsimmon_1.default.seqObj(...panelWithLayout, parsimmon_1.default.all))
            .or(parsimmon_1.default.seqObj(...panelBase, parsimmon_1.default.all))
            .parse(line);
        if (panelResult.status) {
            if (!this.currentDialog) {
                throw new Error('Dialog not set');
            }
            this.currentPanel = panelResult.value.name;
            this.result.dialogs[this.currentDialog].panels[this.currentPanel] = {
                layout: panelResult.value.layout,
                condition: panelResult.value.condition,
                fields: [],
                panels: {},
            };
            return;
        }
        const fieldBase = [
            parsimmon_1.default.string('field'),
            this.space, this.equal, this.space,
            ['title', this.notQuote.wrap(...this.quotes)],
        ];
        const fieldWithName = [
            ...fieldBase,
            ...this.delimiter,
            ['name', this.name],
        ];
        const fieldWithCondition = [
            ...fieldWithName,
            ...this.delimiter,
            ['condition', this.expression],
        ];
        const fieldWithDoubleCondition = [
            ...fieldWithName,
            ...this.delimiter,
            parsimmon_1.default.regexp(/{.*?}/),
            ...this.delimiter,
            ['condition', this.expression],
        ];
        const fieldResult = parsimmon_1.default
            .seqObj(...fieldWithDoubleCondition, parsimmon_1.default.all)
            .or(parsimmon_1.default.seqObj(...fieldWithCondition, parsimmon_1.default.all))
            .or(parsimmon_1.default.seqObj(...fieldWithName, parsimmon_1.default.all))
            .or(parsimmon_1.default.seqObj(...fieldBase, parsimmon_1.default.all))
            .parse(line);
        if (fieldResult.status) {
            if (!this.currentDialog) {
                throw new Error('Dialog not set');
            }
            this.result.dialogs[this.currentDialog].fields.push({
                name: fieldResult.value.name || '_fieldText_',
                title: INI.sanitize(fieldResult.value.title),
                condition: fieldResult.value.condition,
            });
            return;
        }
        const helpResult = parsimmon_1.default
            .seqObj(parsimmon_1.default.string('topicHelp'), this.space, this.equal, this.space, ['help', this.notQuote.wrap(...this.quotes)], parsimmon_1.default.all)
            .parse(line);
        if (!this.currentDialog) {
            throw new Error('Dialog not set');
        }
        if (helpResult.status) {
            this.result.dialogs[this.currentDialog].help = INI.sanitize(helpResult.value.help);
        }
    }
    parseKeyValueFor(section, line) {
        const { key, value } = this.parseKeyValue(line);
        if (this.result[section][key]) {
            return;
        }
        this.result[section][key] = value;
    }
    parseKeyValue(line) {
        const base = [
            ['key', this.name],
            this.space, this.equal, this.space,
        ];
        const result = parsimmon_1.default
            .seqObj(...base, ['value', this.notQuote.wrap(...this.quotes)], parsimmon_1.default.all)
            .or(parsimmon_1.default.seqObj(...base, ['value', this.numbers], parsimmon_1.default.all))
            .tryParse(line);
        return {
            key: result.key,
            value: INI.isNumber(result.value)
                ? Number(result.value)
                : INI.sanitize(result.value),
        };
    }
    parseMenu(line) {
        if (line.startsWith('menuDialog')) {
            return;
        }
        const menuResult = parsimmon_1.default
            .seqObj(parsimmon_1.default.string('menu'), this.space, this.equal, this.space, ['name', this.inQuotes], parsimmon_1.default.all).parse(line);
        if (menuResult.status) {
            const title = INI
                .sanitize(menuResult.value.name)
                .replace(/&/g, '');
            const name = title
                .toLowerCase()
                .replace(/([^\w]\w)/g, (g) => g[1].toUpperCase());
            this.currentMenu = name;
            this.result.menus[this.currentMenu] = {
                title: INI.sanitize(title),
                subMenus: {},
            };
            return;
        }
        if (this.currentMenu) {
            const groupMenuResult = parsimmon_1.default
                .seqObj(parsimmon_1.default.string('groupMenu'), this.space, this.equal, this.space, ['title', this.notQuote.wrap(...this.quotes)], parsimmon_1.default.all)
                .parse(line);
            if (groupMenuResult.status) {
                const title = INI
                    .sanitize(groupMenuResult.value.title);
                const name = title
                    .toLowerCase()
                    .replace(/([^\w]\w)/g, (g) => g[1].toUpperCase());
                this.currentGroupMenu = name;
                this.result.menus[this.currentMenu].subMenus[name] = {
                    type: 'groupMenu',
                    title: INI.sanitize(title),
                    groupChildMenus: {},
                };
                return;
            }
            if (this.currentGroupMenu && line.startsWith('groupChildMenu')) {
                const base = [
                    parsimmon_1.default.string('groupChildMenu'),
                    this.space, this.equal, this.space,
                    ['name', this.name],
                ];
                const withTitle = [
                    ...base,
                    ...this.delimiter,
                    ['title', this.notQuote.wrap(...this.quotes)],
                ];
                const full = [
                    ...withTitle,
                    ...this.delimiter,
                    ['condition', this.expression],
                    parsimmon_1.default.all,
                ];
                const groupChildMenuResult = parsimmon_1.default.seqObj(...full, parsimmon_1.default.all)
                    .or(parsimmon_1.default.seqObj(...withTitle, parsimmon_1.default.all))
                    .or(parsimmon_1.default.seqObj(...base, parsimmon_1.default.all))
                    .tryParse(line);
                this.result.menus[this.currentMenu].subMenus[this.currentGroupMenu].groupChildMenus[groupChildMenuResult.name] = {
                    title: INI.sanitize(groupChildMenuResult.title),
                    condition: groupChildMenuResult.condition ? INI.sanitize(groupChildMenuResult.condition) : '',
                };
                return;
            }
            const base = [
                parsimmon_1.default.string('subMenu'),
                this.space, this.equal, this.space,
                ['name', this.name],
            ];
            const withTitle = [
                ...base,
                ...this.delimiter,
                ['title', this.notQuote.wrap(...this.quotes)],
            ];
            const withPage = [
                ...withTitle,
                ...this.delimiter,
                ['page', parsimmon_1.default.digits],
            ];
            const withCondition = [
                ...withTitle,
                ...this.delimiter,
                ['condition', this.expression],
                parsimmon_1.default.all,
            ];
            const full = [
                ...withPage,
                ...this.delimiter,
                ['condition', this.expression],
            ];
            const subMenuResult = parsimmon_1.default.seqObj(...full, parsimmon_1.default.all)
                .or(parsimmon_1.default.seqObj(...withCondition, parsimmon_1.default.all))
                .or(parsimmon_1.default.seqObj(...withPage, parsimmon_1.default.all))
                .or(parsimmon_1.default.seqObj(...withTitle, parsimmon_1.default.all))
                .or(parsimmon_1.default.seqObj(...base, parsimmon_1.default.all))
                .tryParse(line);
            this.result.menus[this.currentMenu].subMenus[subMenuResult.name] = {
                type: 'subMenu',
                title: INI.sanitize(subMenuResult.title),
                page: Number(subMenuResult.page || 0),
                condition: subMenuResult.condition ? INI.sanitize(subMenuResult.condition) : '',
            };
        }
    }
    parseDefines(line) {
        const result = parsimmon_1.default.seqObj(parsimmon_1.default.string('#define'), this.space, ['name', this.name], this.space, this.equal, this.space, ['values', this.values], parsimmon_1.default.all).tryParse(line);
        this.result.defines[result.name] = result.values.map(INI.sanitize);
        const resolved = this.result.defines[result.name].map((val) => (val.startsWith('$')
            ? this.result.defines[val.slice(1)]
            : val)).flat();
        this.result.defines[result.name] = resolved;
    }
    parsePcVariables(line) {
        if (line.startsWith('#define')) {
            this.parseDefines(line);
            return;
        }
        const result = this.parseConstAndVar(line, true);
        let constant = {};
        switch (result.type) {
            case 'scalar':
                constant = {
                    type: result.type,
                    size: result.size,
                    units: INI.sanitize(result.units),
                    scale: INI.numberOrExpression(result.scale),
                    transform: INI.numberOrExpression(result.transform),
                    min: INI.numberOrExpression(result.min),
                    max: INI.numberOrExpression(result.max),
                    digits: Number(result.digits),
                };
                break;
            case 'array':
                constant = {
                    type: result.type,
                    size: result.size,
                    shape: INI.arrayShape(result.shape),
                    units: INI.sanitize(result.units),
                    scale: INI.numberOrExpression(result.scale),
                    transform: INI.numberOrExpression(result.transform),
                    min: INI.numberOrExpression(result.min),
                    max: INI.numberOrExpression(result.max),
                    digits: Number(result.digits),
                };
                break;
            case 'bits':
                constant = {
                    type: result.type,
                    size: result.size,
                    address: result.address.split(':').map(Number),
                    values: this.resolveBitsValues(result.name, result.values || []),
                };
                break;
            case 'string':
                constant = {
                    type: result.type,
                    size: result.size,
                    length: Number(result.length),
                };
                break;
            default:
                break;
        }
        this.result.pcVariables[result.name] = constant;
    }
    parseConstants(line) {
        if (line.startsWith('#define')) {
            this.parseDefines(line);
            return;
        }
        const page = parsimmon_1.default
            .seqObj(parsimmon_1.default.string('page'), this.space, this.equal, this.space, ['page', parsimmon_1.default.digits], parsimmon_1.default.all).parse(line);
        if (page.status) {
            this.currentPage = Number(page.value.page) - 1;
            return;
        }
        if (INI.isNumber(this.currentPage)) {
            const result = this.parseConstAndVar(line);
            if (!this.result.constants.pages[this.currentPage]) {
                this.result.constants.pages[this.currentPage] = {
                    number: this.currentPage + 1,
                    size: 0,
                    data: {},
                };
            }
            let constant = {};
            switch (result.type) {
                case 'scalar':
                    constant = {
                        type: result.type,
                        size: result.size,
                        offset: Number(result.offset),
                        units: INI.sanitize(result.units),
                        scale: INI.numberOrExpression(result.scale),
                        transform: INI.numberOrExpression(result.transform),
                        min: INI.numberOrExpression(result.min),
                        max: INI.numberOrExpression(result.max),
                        digits: Number(result.digits),
                    };
                    break;
                case 'array':
                    constant = {
                        type: result.type,
                        size: result.size,
                        offset: Number(result.offset),
                        shape: INI.arrayShape(result.shape),
                        units: INI.sanitize(result.units),
                        scale: INI.numberOrExpression(result.scale),
                        transform: INI.numberOrExpression(result.transform),
                        min: INI.numberOrExpression(result.min),
                        max: INI.numberOrExpression(result.max),
                        digits: Number(result.digits),
                    };
                    break;
                case 'bits':
                    constant = {
                        type: result.type,
                        size: result.size,
                        offset: Number(result.offset),
                        address: result.address.split(':').map(Number),
                        values: this.resolveBitsValues(result.name, result.values || []),
                    };
                    break;
                default:
                    break;
            }
            if (this.result.constants.pages[this.currentPage].data[result.name]) {
                return;
            }
            this.result.constants.pages[this.currentPage].data[result.name] = constant;
        }
    }
    resolveBitsValues(name, values) {
        return values.map((val) => {
            const resolve = () => {
                const defineName = INI.sanitize(val.slice(1));
                const resolved = this.result.defines[defineName];
                if (!resolved) {
                    throw new Error(`Unable to resolve bits values for ${name}`);
                }
                return resolved;
            };
            return val.startsWith('$') ? resolve() : INI.sanitize(val);
        }).flat().filter((val) => val !== '');
    }
    parseConstAndVar(line, asPcVariable = false) {
        const address = [
            ['address', parsimmon_1.default.regexp(/\d+:\d+/).trim(this.space).wrap(...this.sqrBrackets)],
        ];
        const base = (type) => {
            let list = [
                ['name', this.name],
                this.space, this.equal, this.space,
                ['type', parsimmon_1.default.string(type)],
                ...this.delimiter,
                ['size', this.size],
            ];
            if (!asPcVariable) {
                list = [
                    ...list,
                    ...[
                        ...this.delimiter,
                        ['offset', parsimmon_1.default.digits],
                    ],
                ];
            }
            return list;
        };
        const scalarShortRest = [
            ['units', parsimmon_1.default.alt(this.expression, this.inQuotes)],
            ...this.delimiter,
            ['scale', parsimmon_1.default.alt(this.expression, this.numbers)],
            ...this.delimiter,
            ['transform', parsimmon_1.default.alt(this.expression, this.numbers)],
        ];
        const scalarRest = [
            ...scalarShortRest,
            ...this.delimiter,
            ['min', parsimmon_1.default.alt(this.expression, this.numbers)],
            ...this.delimiter,
            ['max', parsimmon_1.default.alt(this.expression, this.numbers)],
            ...this.delimiter,
            ['digits', parsimmon_1.default.alt(this.expression, parsimmon_1.default.digits)],
            parsimmon_1.default.all,
        ];
        const scalar = parsimmon_1.default.seqObj(...base('scalar'), ...this.delimiter, ...scalarRest);
        const scalarShort = parsimmon_1.default.seqObj(...base('scalar'), ...this.delimiter, ...scalarShortRest, parsimmon_1.default.all);
        const array = parsimmon_1.default.seqObj(...base('array'), ...this.delimiter, ['shape', parsimmon_1.default.regexp(/\d+\s*(x\s*\d+)*/).trim(this.space).wrap(...this.sqrBrackets)], ...this.delimiter, ...scalarRest);
        const bits = parsimmon_1.default.seqObj(...base('bits'), ...this.delimiter, ...address, ...this.delimiter, ['values', this.values], parsimmon_1.default.all);
        const bitsShort = parsimmon_1.default.seqObj(...base('bits'), ...this.delimiter, ...address, parsimmon_1.default.all);
        const string = parsimmon_1.default.seqObj(...base('string'), ...this.delimiter, ['length', parsimmon_1.default.digits], parsimmon_1.default.all);
        const continuousChannelValue = parsimmon_1.default.seqObj(['name', this.name], this.space, this.equal, this.space, ['reference', this.name], ...this.delimiter, ['channel', this.name], parsimmon_1.default.all);
        return scalar
            .or(scalarShort)
            .or(array)
            .or(bits)
            .or(bitsShort)
            .or(string)
            .or(continuousChannelValue)
            .tryParse(line);
    }
}
exports.INI = INI;
INI.numberOrExpression = (val) => INI.isNumber(val || '0') ? Number(val || 0) : INI.sanitize(`${val}`);
INI.sanitize = (val) => val === undefined ? '' : `${val}`
    .replace(/"/g, '')
    .replace(/\s+/g, ' ')
    .trim();
INI.isNumber = (val) => !Number.isNaN(Number(val));
INI.arrayShape = (val) => {
    const parts = INI.sanitize(val).split('x');
    return {
        columns: Number(parts[0]),
        rows: parts[1] ? Number(parts[1]) : 0,
    };
};
